import { useEffect, useMemo, useState } from "react";
import {
  documentsPourCodeClient,
  evenementDepuisDocument
} from "./interclubCycle";
import {
  documentsInterclubMetierUniques,
  certifierDetteDepuisSituationsInterclub,
  mouvementsClubCanoniques
} from "./dashboardCrmUtils";
import { mouvementsPaiementInterclub, syntheseCaInterclubV2 } from "./interclubDirectSync";
import "./CycleCommercialInterclub.css";

const couleurs = {
  Devis: "#1565c0",
  Commande: "#2e7d32",
  Livraison: "#ef6c00",
  Facture: "#00695c",
  Avoir: "#111",
  Règlement: "#2e7d32"
};

function formatMontant(valeur) {
  if (valeur === undefined || valeur === null || valeur === "") return "";
  return `${Number(valeur || 0).toFixed(2)} €`;
}

function formatDateHeure(valeur) {
  if (!valeur) return "Non disponible";
  return new Date(valeur).toLocaleString("fr-FR");
}

export default function CycleCommercialInterclub({
  clientSupabase,
  club,
  documentsInterclub,
  actions,
  recherche,
  erreurInterclub,
  onSupprimerAction
}) {
  const [soldeOuvert, setSoldeOuvert] = useState(false);
  const [situationDirecte, setSituationDirecte] = useState(null);
  const [mappingSituation, setMappingSituation] = useState("NON RAPPROCHÉ");
  const [lectureId, setLectureId] = useState(null);
  const [actualisationEnCours, setActualisationEnCours] = useState(false);
  const [messageActualisation, setMessageActualisation] = useState("");
  const codeClient = club?.numero_compte_interclub || situationDirecte?.code_client || "";
  const documentsClub = useMemo(
    () => documentsPourCodeClient(documentsInterclub, codeClient),
    [documentsInterclub, codeClient]
  );
  const documentsDirects = useMemo(() => (situationDirecte?.documents || []).map((document, index) => ({
    id: `direct-${document.type_document}-${document.identifiant_source || index}`,
    type_document: document.type_document,
    identifiant_source: document.identifiant_source,
    donnees: document.donnees || {},
    derniere_observation: situationDirecte.actualise_le,
    updated_at: situationDirecte.actualise_le
  })), [situationDirecte]);
  const documentsFusionnes = useMemo(
    () => documentsInterclubMetierUniques([...documentsClub, ...documentsDirects]),
    [documentsClub, documentsDirects]
  );
  const mouvementsCanoniques = useMemo(
    () => mouvementsClubCanoniques(documentsFusionnes, club),
    [documentsFusionnes, club]
  );
  const codeClientNormalise = String(codeClient || "").trim();
  const codesClub = useMemo(() => new Set(codeClientNormalise ? [codeClientNormalise] : []), [codeClientNormalise]);
  const sourceFacturesDisponible = (documentsInterclub || []).some((document) => ["FACTURE", "AVOIR"].includes(document?.type_document));
  const sourcePaiementsDisponible = (documentsInterclub || []).some((document) => document?.type_document === "PAIEMENT");
  const sourceCommandesDisponible = (documentsInterclub || []).some((document) => document?.type_document === "COMMANDE");
  const financeCanonique = useMemo(() => syntheseCaInterclubV2(documentsFusionnes, codesClub, {
    codesClientsConnus: codesClub,
    inclureNonRapproches: false
  }).mois, [documentsFusionnes, codesClub]);
  const paiementsCanoniques = useMemo(() => mouvementsPaiementInterclub(documentsFusionnes, codesClub, {
    codesClientsConnus: codesClub,
    inclureNonRapproches: false
  }), [documentsFusionnes, codesClub]);

  const chargerSituationDirecte = async () => {
    if (!clientSupabase || !club?.id) return null;
    const champs = "club_id,code_client,solde,resume,ecritures,documents,ecarts,actualise_le";
    const { data: parId, error: erreurId } = await clientSupabase
      .from("interclub_situations_clients")
      .select(champs)
      .eq("club_id", club.id)
      .limit(2);
    if (erreurId) return null;
    if ((parId || []).length > 1) {
      setSituationDirecte(null);
      setMappingSituation("AMBIGU");
      return null;
    }
    if ((parId || []).length === 1) {
      setSituationDirecte(parId[0]);
      setMappingSituation("RAPPROCHÉ");
      return parId[0];
    }
    const code = String(club?.numero_compte_interclub || "").trim();
    if (!code) {
      setSituationDirecte(null);
      setMappingSituation("NON RAPPROCHÉ");
      return null;
    }
    const { data: parCode, error: erreurCode } = await clientSupabase
      .from("interclub_situations_clients")
      .select(champs)
      .eq("code_client", code)
      .limit(2);
    if (erreurCode) return null;
    if ((parCode || []).length === 1) {
      setSituationDirecte(parCode[0]);
      setMappingSituation("RAPPROCHÉ");
      return parCode[0];
    }
    setSituationDirecte(null);
    setMappingSituation((parCode || []).length > 1 ? "AMBIGU" : "NON RAPPROCHÉ");
    return null;
  };

  useEffect(() => {
    setSituationDirecte(null);
    setMappingSituation("NON RAPPROCHÉ");
    setLectureId(null);
    setActualisationEnCours(false);
    setMessageActualisation("");
    chargerSituationDirecte();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSupabase, club?.id]);

  useEffect(() => {
    if (!lectureId || !clientSupabase) return undefined;
    let actif = true;
    let tentative = 0;
    let timer;
    const verifier = async () => {
      if (!actif) return;
      tentative += 1;
      const { data, error } = await clientSupabase
        .from("interclub_lectures_clients")
        .select("statut,erreur_technique")
        .eq("id", lectureId)
        .maybeSingle();
      if (!actif) return;
      if (error) {
        setMessageActualisation("Actualisation impossible. Dernière situation connue conservée.");
        setActualisationEnCours(false);
        return;
      }
      if (data?.statut === "TERMINEE") {
        await chargerSituationDirecte();
        setMessageActualisation("Situation Interclub actualisée.");
        setActualisationEnCours(false);
        setLectureId(null);
        return;
      }
      if (["ERREUR", "INTERCLUB_INDISPONIBLE"].includes(data?.statut)) {
        setMessageActualisation("Interclub indisponible. Dernière situation connue conservée.");
        setActualisationEnCours(false);
        setLectureId(null);
        return;
      }
      if (tentative >= 60) {
        setMessageActualisation("Lecture en attente de l’agent local. Dernière situation connue conservée.");
        setActualisationEnCours(false);
        return;
      }
      timer = setTimeout(verifier, 2000);
    };
    verifier();
    return () => { actif = false; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lectureId, clientSupabase]);

  const actualiserInterclub = async () => {
    if (!clientSupabase || !club?.id || actualisationEnCours) return;
    setActualisationEnCours(true);
    setMessageActualisation("Demande transmise à l’agent local…");
    const { data, error } = await clientSupabase.rpc("interclub_client_actualiser", { p_club_id: club.id });
    if (error || !data) {
      setActualisationEnCours(false);
      setMessageActualisation(error?.message || "Actualisation Interclub impossible.");
      return;
    }
    setLectureId(data);
  };

  const certificationDette = useMemo(() => {
    if (!situationDirecte) return null;
    const fin = String(situationDirecte.actualise_le || "").slice(0, 10);
    return certifierDetteDepuisSituationsInterclub([situationDirecte], documentsFusionnes, [club], { fin });
  }, [situationDirecte, documentsFusionnes, club]);
  const facturesDetteCanoniques = certificationDette?.certifiable
    ? certificationDette.factures.filter((facture) => String(facture.club_id) === String(club?.id))
    : [];
  const detteCanonique = certificationDette?.certifiable
    ? facturesDetteCanoniques.reduce((total, facture) => total + Number(facture.reste_a_payer || 0), 0)
    : null;

  const actionsNonFinancieres = useMemo(() => (actions || []).filter((action) =>
    !["Facturé", "Payé", "Commande", "Livraison", "Avoir"].includes(action.type)
  ), [actions]);
  const evenementsFinanciers = useMemo(() => documentsFusionnes
    .map(evenementDepuisDocument)
    .filter(Boolean)
    .sort((a, b) => String(b.date || "").localeCompare(String(a.date || ""))), [documentsFusionnes]);

  const rechercheNormalisee = String(recherche || "").trim().toLowerCase();
  const correspondRecherche = (valeurs) => !rechercheNormalisee || valeurs
    .some((valeur) => String(valeur || "").toLowerCase().includes(rechercheNormalisee));
  const evenementsFinanciersFiltres = evenementsFinanciers.filter((event) => correspondRecherche([
    event.type, event.numero_document, event.numero_commande, event.commentaire, event.statut
  ]));
  const actionsHistoriquesFiltrees = actionsNonFinancieres.filter((action) => correspondRecherche([
    action.type, action.numero_document, action.commentaire, action.date
  ]));

  return <>
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", margin: "8px 0" }}>
      <button
        type="button"
        onClick={() => setSoldeOuvert(true)}
        style={{ padding: 9, border: "1px solid #00695c", color: "#00695c", background: "white", borderRadius: 6, fontWeight: "bold" }}
      >
        SOLDE INTERCLUB
      </button>
      <button
        type="button"
        onClick={actualiserInterclub}
        disabled={!club?.id || actualisationEnCours}
        style={{ padding: 9, border: 0, color: "white", background: actualisationEnCours ? "#78909c" : "#00695c", borderRadius: 6, fontWeight: "bold" }}
      >
        {actualisationEnCours ? "ACTUALISATION…" : "ACTUALISER INTERCLUB"}
      </button>
      {!codeClient && <strong style={{ color: "#b71c1c" }}>Compte Interclub non rattaché</strong>}
      {mappingSituation === "AMBIGU" && <strong style={{ color: "#b71c1c" }}>MAPPING À CONTRÔLER</strong>}
      {codeClient && erreurInterclub && <span style={{ color: "#b71c1c" }}>Interclub indisponible — dernière situation non chargée</span>}
      {codeClient && !erreurInterclub && <span style={{ fontSize: 12 }}>
        Source : Interclub · Actualisé le : {formatDateHeure(situationDirecte?.actualise_le)} · Fraîcheur : {situationDirecte?.actualise_le ? "DATÉE" : "NON CERTIFIÉE"}
      </span>}
      {messageActualisation && <span style={{ fontSize: 12, color: messageActualisation.includes("actualisée") ? "#2e7d32" : "#6d4c41" }}>{messageActualisation}</span>}
    </div>

    <div className="cci-summary" aria-label="Synthèse canonique du club">
      <span><small>Devis</small><b>{mouvementsCanoniques.synthese.devis.nombre} · {formatMontant(mouvementsCanoniques.synthese.devis.montant)}</b></span>
      <span><small>Commandes</small><b>{mouvementsCanoniques.synthese.commandes.nombre} · {formatMontant(mouvementsCanoniques.synthese.commandes.montant)}</b></span>
      <span><small>Facturé</small><b>{sourceFacturesDisponible && codeClient ? formatMontant(financeCanonique.caTTC) : "—"}</b>{(!sourceFacturesDisponible || !codeClient) && <small>NON CERTIFIÉ</small>}</span>
      <span className="paid"><small>Payé</small><b>{sourcePaiementsDisponible && codeClient ? formatMontant(financeCanonique.encaisse) : "—"}</b>{(!sourcePaiementsDisponible || !codeClient) && <small>NON CERTIFIÉ</small>}</span>
      <span className={detteCanonique != null && detteCanonique > 0.009 ? "due" : "settled"}><small>Reste dû</small><b>{detteCanonique == null ? "—" : formatMontant(detteCanonique)}</b>{detteCanonique == null && <small>DONNÉES DETTE NON SYNCHRONISÉES</small>}</span>
      <span><small>À facturer</small><b>{sourceCommandesDisponible && codeClient ? `${mouvementsCanoniques.synthese.aFacturer.nombre} · ${formatMontant(mouvementsCanoniques.synthese.aFacturer.montant)}` : "—"}</b>{(!sourceCommandesDisponible || !codeClient) && <small>NON CERTIFIÉ</small>}</span>
    </div>

    {mouvementsCanoniques.aFacturer.lignes.length > 0 && <details className="cci-movements"><summary>Commandes confirmées à facturer ({mouvementsCanoniques.aFacturer.total.commandes})</summary><div className="cci-table-wrap"><table><thead><tr><th>Commande</th><th>Date confirmation</th><th>Montant commande</th><th>Reste à facturer</th><th>Tranche</th></tr></thead><tbody>
      {mouvementsCanoniques.aFacturer.lignes.map((ligne) => <tr key={ligne.id}><td>{ligne.numeroCommande}</td><td>{ligne.dateConfirmation || "Date indisponible"}</td><td className="amount">{formatMontant(ligne.montantCommande)}</td><td className="amount"><strong>{formatMontant(ligne.montant)}</strong></td><td>{ligne.tranche}</td></tr>)}
    </tbody></table></div></details>}

    <section className="cci-cycles" aria-label="Documents financiers canoniques">
      <h4>DOCUMENTS FINANCIERS CANONIQUES</h4>
      <div className="cci-table-wrap"><table><thead><tr><th>Document</th><th>Date</th><th>Montant TTC</th><th>Statut</th><th>N° document</th><th>Commande</th></tr></thead><tbody>
        {evenementsFinanciersFiltres.map((event, index) => <tr key={`${event.id}-${index}`}>
          <td><strong style={{ color: couleurs[event.type] || "#526781" }}>{event.type}</strong><small>INTERCLUB</small></td>
          <td>{event.date || ""}</td><td className="amount"><strong>{formatMontant(event.montantTTC)}</strong></td>
          <td>{event.statut || event.commentaire || "Non renseigné"}</td>
          <td>{event.numero_document || ""}</td><td>{event.numero_commande || ""}</td>
        </tr>)}
      </tbody></table></div>
      {evenementsFinanciersFiltres.length === 0 && <p>Aucun document financier canonique pour ce club.</p>}
    </section>

    <section className="cci-cycles" aria-label="Historique CRM">
      <h4>HISTORIQUE CRM — SANS IMPACT SUR LES TOTAUX FINANCIERS</h4>
      {actionsHistoriquesFiltrees.map((action) => <article className="cci-order" key={action.id}>
        <header className="cci-order-head"><div><small>{action.type || "Action"}</small><strong>{action.date || "Date non renseignée"}</strong></div>{onSupprimerAction && <button type="button" onClick={() => onSupprimerAction(action)}>Supprimer</button>}</header>
        <p>{action.commentaire || action.numero_document || "Aucun commentaire"}</p>
      </article>)}
      {actionsHistoriquesFiltrees.length === 0 && <p>Aucune action CRM historique pour ce club.</p>}
    </section>

    {soldeOuvert && <div style={fondModal} onClick={() => setSoldeOuvert(false)}>
      <aside style={panneau} onClick={(event) => event.stopPropagation()}>
        <button type="button" onClick={() => setSoldeOuvert(false)} style={{ float: "right" }}>FERMER</button>
        <h3>SOLDE INTERCLUB</h3>
        {!codeClient ? <p><strong>Compte Interclub non rattaché</strong></p> : <>
          <p><strong>SOLDE : {situationDirecte?.solde != null ? formatMontant(situationDirecte.solde) : "— / NON CERTIFIÉ"}</strong></p>
          <p>Solde fourni par Interclub — aucun solde comptable parallèle n’est recalculé.</p>
          <h4>Dette canonique</h4>
          <p><strong>{detteCanonique == null ? "— / NON CERTIFIÉ" : formatMontant(detteCanonique)}</strong></p>
          <h4>Factures ouvertes certifiées ({facturesDetteCanoniques.length})</h4>
          {facturesDetteCanoniques.map((facture) => <p key={facture.id}>
            {facture.numero_facture} — {formatMontant(facture.reste_a_payer)} — {facture.date_echeance || facture.date_facture || "Date indisponible"}
          </p>)}
          {detteCanonique != null && facturesDetteCanoniques.length === 0 && <p>Aucune facture ouverte à la date certifiée.</p>}
          <h4>Avoirs ({mouvementsCanoniques.avoirs.length})</h4>
          {mouvementsCanoniques.avoirs.map((document) => <p key={document.id}>
            {document.donnees?.numero_facture} — {formatMontant(document.donnees?.montant_ttc)}
            {!document.donnees?.numero_commande && " — facture d’origine non déterminée"}
          </p>)}
          <h4>Commandes confirmées à facturer ({mouvementsCanoniques.aFacturer.total.commandes})</h4>
          {mouvementsCanoniques.aFacturer.lignes.map((ligne) => <p key={ligne.id}>{ligne.numeroCommande} — {formatMontant(ligne.montant)} — {ligne.dateConfirmation || "date indisponible"}</p>)}
          <h4>Paiements canoniques ({paiementsCanoniques.length})</h4>
          {paiementsCanoniques.slice(-20).reverse().map((paiement) => <p key={paiement.identifiant}>
            {paiement.date || ""} — {paiement.numeroFacture || paiement.identifiant} — {formatMontant(paiement.montantTTC)}
          </p>)}
          {paiementsCanoniques.length === 0 && <p>Aucun paiement canonique détaillé.</p>}
          <p><strong>Source : Interclub</strong><br/>Dernière situation Interclub connue : {formatDateHeure(situationDirecte?.actualise_le)}</p>
          {erreurInterclub && <p style={{ color: "#b71c1c" }}>Interclub inaccessible : aucune donnée ancienne n’est présentée comme actuelle.</p>}
        </>}
      </aside>
    </div>}
  </>;
}

const fondModal = { position: "fixed", inset: 0, background: "rgba(0,0,0,.45)", zIndex: 12000, display: "flex", justifyContent: "flex-end" };
const panneau = { width: "min(520px,90vw)", height: "100%", overflowY: "auto", background: "white", padding: 20, boxSizing: "border-box", boxShadow: "-4px 0 12px rgba(0,0,0,.25)" };
